/**
 * How often a page re-asks the server, decided by whether anything is happening.
 *
 * The server is already fast where it matters — the live feed is polled every five seconds while a
 * draft is running or a series is between maps. **The browser was the bottleneck**: a five-second
 * observation was being shown to a reader once a minute, so the site was up to a minute stale at
 * exactly the moments it exists for. A draft is worth up to fifteen points and lasts about two
 * minutes; a minute of that is half the opportunity.
 *
 * The other direction matters just as much. Most of the day nothing is on, and polling every fifteen
 * seconds then is a page that costs more and shows nothing new — so the pace is decided by the data
 * already in hand rather than fixed. Every query below pays the fast rate only while its own answer
 * says something is live.
 */

/** While a game is being played, drafted, or is minutes away. */
export const LIVE_MS = 15_000

/** Nothing on. Most of the day. */
export const CALM_MS = 60_000

/**
 * A kick-off this close is treated as live even with nothing observed yet.
 *
 * The published time is a third party's estimate and the lobby opens around it, so the window has to
 * open before the clock says it should — and it is the browser's only signal until `lobbySeenAt`
 * appears.
 */
const IMMINENT_SECONDS = 20 * 60

export const pace = (live: boolean): number => (live ? LIVE_MS : CALM_MS)

/**
 * Is any of these fixtures live, drafting, or about to start?
 *
 * Reads the observations rather than the clock wherever it can. `lobbySeenAt` means Valve has
 * announced the game — which is a fact, unlike the schedule — and `gameStartedAt` means it is under
 * way. The clock is the fallback for a fixture nothing has been seen of yet.
 */
export const fixturesAreLive = (
  fixtures:
    | readonly {
        startTime: number
        lobbySeenAt?: number | null
        draftSeenAt?: number | null
        gameStartedAt?: number | null
      }[]
    | undefined,
): boolean => {
  if (!fixtures?.length) {
    return false
  }

  const now = Date.now() / 1000
  return fixtures.some(
    (f) =>
      f.lobbySeenAt != null ||
      f.draftSeenAt != null ||
      f.gameStartedAt != null ||
      f.startTime - now < IMMINENT_SECONDS,
  )
}

/** Is any of these series being played right now? */
export const seriesAreLive = (
  series: readonly { isLive: boolean; maps?: readonly { status: string }[] }[] | undefined,
): boolean =>
  series?.some((s) => s.isLive || s.maps?.some((m) => m.status === 'live')) ?? false
